import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex justify-between items-center mb-4" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", null, [
  /*#__PURE__*/_createElementVNode("h3", { class: "ui-card__title" }, "Team members"),
  /*#__PURE__*/_createElementVNode("p", { class: "ui-card__subtitle" }, " Manage team members in your company ")
], -1)
const _hoisted_3 = { class: "flex items-center" }
const _hoisted_4 = { class: "h-10 w-10 relative flex-shrink-0 mr-2" }
const _hoisted_5 = { class: "flex flex-col" }
const _hoisted_6 = { class: "text-xs font-medium text-dark-blue-500 opacity-50" }
const _hoisted_7 = { class: "flex items-center" }
const _hoisted_8 = { key: 1 }
const _hoisted_9 = { key: 1 }

import { useCompanyProfile } from '@/account';
import { useTeamSeats } from '@/billing/team-seats/use-team-seats';
import { WorkerActivityType } from '@/phone-numbers';
import {
	SvgIcon,
	UiActivityDot,
	UiAvatar,
	UiButton,
	UiCard,
	UiConfirmation,
	UiLine,
	UiSelect,
	useConfirmation,
	useLoading
} from '@/ui-kit';
import { computed, onBeforeMount, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useTeams } from '../composition/use-teams';
import { TeamMember } from '../types';
import { useBilling } from '@/billing';


export default /*#__PURE__*/_defineComponent({
  __name: 'TeamMembers',
  setup(__props) {

const confirmationRef =
	ref<InstanceType<typeof UiConfirmation>>();
const router = useRouter();
const { userId, isManager, companyId, isPaidAccount } =
	useCompanyProfile();
const state = useTeamSeats();
const billing = useBilling();
const {
	pagination,
	paginate,
	deleteTeamMember,
	editTeamMember,
	allItems,
	loading,
	changePageSize,
	loadRoles,
	loadTeam,
	roleOptions
} = useTeams();

const { loading: deleteLoading, toggleLoading } =
	useLoading();

onBeforeMount(() => {
	if (!companyId.value) {
		return;
	}
	loadRoles();
	loadTeam(companyId.value);
});

function changeRole(member: TeamMember, id: number) {
	const company = useCompanyProfile();
	editTeamMember(
		company.companyId!.value as number,
		member.id,
		{
			role: id
		} as any
	);
}

async function triggerDeleteTeamMember(member: TeamMember) {
	const { success } = useToast();
	const result = await confirmationRef.value?.openDialog(
		`Are you sure you want to delete ${member.name} from team members?`,
		'This action <b>can`t be undone</b>.'
	);
	if (result) {
		toggleLoading(true);
		await deleteTeamMember(
			companyId.value!,
			member.id as number
		);
		toggleLoading(false);
		confirmationRef.value?.closeDialog();
		success('Contact was successfully deleted.');
	}
}

const { openConfirmation, closeConfirmation } =
	useConfirmation();

async function addTeamMember() {
	if (!isPaidAccount.value) {
		return router.push({ name: 'AddTeamMember' });
	}

	if (
		(!billing.currentSubscription.value &&
			isPaidAccount.value) ||
		state.totalAvailableTeamSeats.value <=
			allItems.value.length
	) {
		const result = await openConfirmation(
			'You have reached your team members limit.',
			'On trial you have only one team member. To add more team members, please upgrade your subscription',
			{
				confirmTheme: 'primary',
				confirmText: 'Upgrade plan'
			}
		);

		if (result) {
			router.push({ name: 'CurrentSubscription' });
			closeConfirmation();
		}
	} else {
		router.push({ name: 'AddTeamMember' });
	}
}

return (_ctx: any,_cache: any) => {
  const _component_el_tooltip = _resolveComponent("el-tooltip")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(UiCard), {
      theme: "white",
      class: "relative pt-4 px-4 flex-grow",
      contentClass: "flex flex-col"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _hoisted_2,
          _createVNode(_unref(UiButton), {
            onClick: addTeamMember,
            class: "!text-sm !h-10 !px-2",
            theme: "primary"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(SvgIcon), {
                class: "h-5 w-5 mr-2",
                name: "only-plus"
              }),
              _createTextVNode("Add Team Member ")
            ]),
            _: 1
          })
        ]),
        _createVNode(_unref(UiLine)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(allItems), (member) => {
          return (_openBlock(), _createElementBlock("div", {
            key: member.id,
            class: "group flex justify-between items-center mb-1.5 cursor-pointer hover:bg-dark-blue-400 hover:bg-opacity-10 py-1 px-2 rounded-md transition"
          }, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_unref(UiAvatar), {
                  class: "min-h-10 h-10 w-10 min-w-10",
                  avatar: member?.avatar?.path,
                  name: member.name
                }, null, 8, ["avatar", "name"]),
                _createVNode(_unref(UiActivityDot), {
                  class: "absolute right-0 bottom-0",
                  status: 
							member.isOnline
								? _unref(WorkerActivityType).Available
								: _unref(WorkerActivityType).Unavailable
						
                }, null, 8, ["status"])
              ]),
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("span", null, _toDisplayString(member.name), 1),
                _createElementVNode("span", _hoisted_6, _toDisplayString(member.email), 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_7, [
              (member.id !== _unref(userId))
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    (
							member.role?.name !== 'Owner' &&
							!_unref(isManager)
						)
                      ? (_openBlock(), _createBlock(_unref(UiSelect), {
                          key: 0,
                          placeholder: "Choose role",
                          theme: "transparent",
                          class: "!m-0",
                          name: "role",
                          disabled: !_unref(roleOptions).length,
                          modelValue: member.role?.id,
                          options: _unref(roleOptions),
                          "onUpdate:modelValue": ($event: any) => (
							changeRole(member, $event)
						)
                        }, null, 8, ["disabled", "modelValue", "options", "onUpdate:modelValue"]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(member.role?.name || 'Not Set.'), 1))
                  ], 64))
                : (_openBlock(), _createElementBlock("div", _hoisted_9, "You")),
              _createElementVNode("div", {
                class: _normalizeClass([
						'ml-2 opacity-0 pr-2 flex',
						{
							'group-hover:opacity-100':
								member.role.name !==
									'Owner' &&
								member.id !== _unref(userId) &&
								!_unref(isManager)
						}
					])
              }, [
                _createVNode(_component_el_tooltip, {
                  disabled: 
							member.role.name === 'Owner' ||
							member.id === _unref(userId) ||
							_unref(isManager)
						,
                  "show-after": 500,
                  "hide-after": 0,
                  enterable: false,
                  content: "Delete team member"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(SvgIcon), {
                      class: "h-5 w-5 outline-none",
                      name: "close",
                      onClick: ($event: any) => (
								triggerDeleteTeamMember(
									member
								)
							)
                    }, null, 8, ["onClick"])
                  ]),
                  _: 2
                }, 1032, ["disabled"])
              ], 2)
            ])
          ]))
        }), 128))
      ]),
      _: 1
    }),
    _createVNode(_unref(UiConfirmation), {
      ref_key: "confirmationRef",
      ref: confirmationRef,
      loading: _unref(deleteLoading)
    }, null, 8, ["loading"])
  ], 64))
}
}

})